import { GridConstantsFactory } from 'src/app/base/shared/syncfusion/grid-constants-factory';
import { GridBaseComponent } from 'src/app/base/shared/grid-base/grid-base.component';
import { CustomValidatorsFactory } from 'src/app/base/shared/validators/custom-validators-factory';
import { UserManagementData } from '../models/user-management-data';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { DeleteEventArgs, RowDataBoundEventArgs, GridModule, CommandModel, CommandClickEventArgs } from '@syncfusion/ej2-angular-grids';
import { UserManagementService } from './user-management.service';
import { AvpCustomAdaptor } from '../shared/syncfusion/avp-custom-adaptor';
import { DataManager } from '@syncfusion/ej2-data';
import { ClientService } from '../services/client/client.service';
import {
  ClientDTO,
  CreateProxyUserDTO,
  CreateUserDTO,
  ICreateMobileUserDTO,
  ICreateProxyUserDTO,
  ICreateThirdPartyUserDTO,
  ICreateUserDTO,
  IUpdateProxyUserDTO,
  IUpdateThirdPartyUserDTO,
  IUpdateUserInfoDTO,
  Role,
  UpdateProxyUserDTO,
  UpdateUserInfoDTO,
  UserInfoDTO,
} from '../models/models.swagger.generated';
import { Component, HostListener, OnInit } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { AuthService } from '../services/auth.service';
import { ActivatedRoute, Router } from '@angular/router';
import { parkerRegistrationRoute } from '../../core/routing/route-names';
import { SpinnerService } from '../spinner/spinner.service';
import { DateTime } from 'luxon';
import { SfAction, SfRequestTypes } from '../models/syncfusion-data';
import { ProxyEditFormComponent } from './proxy-edit-form/proxy-edit-form.component';
import { UserEditFormComponent } from './user-edit-form/user-edit-form.component';
import { ToolbarModule } from '@syncfusion/ej2-angular-navigations';
import { CheckBoxModule } from '@syncfusion/ej2-angular-buttons';
import { SearchFieldComponent } from '../shared/search-field/search-field.component';
import { ClientAutocompleteComponent } from '../shared/client-autocomplete/client-autocomplete.component';
import { TitleBarComponent } from '../shared/title-bar/title-bar.component';
import { UserEditFormType } from './user-edit-form/user-edit-form-type';
import { ThirdPartyUserEditFormComponent} from './third-party-user-edit-form/third-party-user-edit-form.component';
import { ModalService } from '../shared/modal/modal.service';

@Component({
    selector: 'app-user-management',
    templateUrl: './user-management.component.html',
    styleUrls: ['./user-management.component.scss'],
    imports: [TitleBarComponent, ClientAutocompleteComponent, SearchFieldComponent, CheckBoxModule, GridModule, ToolbarModule, UserEditFormComponent, ProxyEditFormComponent, ThirdPartyUserEditFormComponent, TranslateModule]
})
export class UserManagementComponent extends GridBaseComponent implements OnInit {
  public languageFilter = GridConstantsFactory.createCheckBoxFilter();
  public roleFilter = GridConstantsFactory.createCheckBoxFilter();
  public accountTypeFilter = GridConstantsFactory.createCheckBoxFilter();

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public Role = Role;
  public isUserAdmin = this.authService.isUserAdmin();

  public customGridCommands: CommandModel[];

  private addUserRole = Role.ClientUser;
  private userId: string;

  constructor(
    private fb: FormBuilder,
    private userManagementService: UserManagementService,
    translateService: TranslateService,
    private authService: AuthService,
    clientService: ClientService,
    private route: ActivatedRoute,
    private router: Router,
    private spinnerService: SpinnerService,
    private modalService: ModalService) {

    super(clientService, translateService, userManagementService);
    this.searchOptions = { fields: ['firstname', 'lastname', 'client.name', 'email'] };
    this.customGridCommands = [{
        type: 'Edit',
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-edit e-icons'
        }
      },
      {
        type: 'Delete',
        buttonOption:
        {
          cssClass: 'icon-btn',
          iconCss: 'icon-trash-can e-icons e-danger'
        }
      }
    ] as CommandModel[];

    if (this.authService.isUserAdminOrClientAdminOrCompanyAdmin()) {
      this.customGridCommands.unshift({
        title: 'Reset 2FA',
        type: 'None',
        buttonOption:
        {
          cssClass: 'icon-btn reset-2fa-btn',
          iconCss: 'icon-reset e-icons'
        }
      });
    }
  }

  @HostListener('window:beforeunload')
  beforeUnload() {
    return !this.gridEditForm?.dirty;
  }

  public override get selectedClient(): ClientDTO {
    return this.clientService.selectedClient;
  }

  public override set selectedClient(value: ClientDTO) {
    this.clientService.selectedClient = value;
    this.onClientSelected(value);
  }

  public get useProxyUserForm(): boolean {
    return this.gridEditForm?.controls.role?.value === Role.Proxy;
  }

  public get useThirdPartyUserForm(): boolean {
    return this.gridEditForm?.controls.role?.value === Role.ThirdPartyParkingControl;
  }

  public ngOnInit() {
    this.userId = this.authService.getUserInfo().userId;
    if (this.authService.isUserAdmin()) {
      const editIndex = this.customGridCommands.findIndex(c => c.type === 'Edit');
      this.customGridCommands.splice(editIndex, 0, {
        type: 'None',
        title: this.translateService.instant('i18n.base.user-management.buttons.show-registrations'),
        buttonOption: {
          cssClass: 'icon-btn show-registrations-btn',
          iconCss: 'icon-search e-icons'
        }
      } as CommandModel);
    }
    this.data = new DataManager({
      adaptor: new AvpCustomAdaptor(this.userManagementService, this.spinnerService)
    });

    this.route.queryParams.subscribe(params => {
      const clientId = +params.clientId;
      const newSelectedClient = this.clientService.clients?.find(c => c.id === clientId);
      if (newSelectedClient) {
        this.clientService.selectedClient = newSelectedClient;
      }
    });

    //refresh grid when cancel is pressed
    this.unsubscribeSubject.subscribe(() => {
      this.grid.refresh();
    });

    this.isActiveColumnName = 'enabled';
    this.editDialogTitle = 'i18n.base.user-add-modal.titleEdit';
    this.addDialogTitle = 'i18n.base.user-add-modal.titleAdd';
  }

  public onRowDataBound(args: RowDataBoundEventArgs): void {
    // hide delete button for current user
    if((args.data as any)?.userId == this.userId) {
      const elementCollection = args.row.getElementsByClassName('e-deletebutton');
      elementCollection[0].classList.add('button-invisible');
    }
    if (!(args.data as UserInfoDTO)?.twoFactorAuthActive) {
      const elementCollection = args.row.getElementsByClassName('reset-2fa-btn');
      elementCollection[0].classList.add('button-invisible');
    }
    if (!(args.data as UserInfoDTO)?.controlPlates?.length) {
      const btn = args.row.getElementsByClassName('show-registrations-btn')[0];
      if (btn) btn.classList.add('button-invisible');
    }
  }

  public override actionBegin(args: DeleteEventArgs): void {
    if (args.requestType === SfRequestTypes.Delete && this.userId === (args.data[0] as any)?.userId) {
      args.cancel = true;
    } else {
      super.actionBegin(args);
    }
  }

  public addUser(role: Role) {
    this.addUserRole = role;
    if (role === Role.ClientUser) {
      this.addDialogTitle = 'i18n.base.user-add-modal.titleAddWebUser';
    }
    if (role === Role.Proxy) {
      this.addDialogTitle = 'i18n.base.user-add-modal.titleAddProxy';
    }
    if (role === Role.ParkingRanger) {
      this.addDialogTitle = 'i18n.base.user-add-modal.titleAddParkingRanger';
    }
    if (role === Role.ThirdPartyParkingControl) {
      this.addDialogTitle = 'i18n.base.user-add-modal.titleAddThirdPartyUser';
    }
    this.onAdd();
  }

  public formatRoles(userInfo: UserInfoDTO): string {
    if (!userInfo || !userInfo.roles) {
      return '';
    }
    return userInfo.roles.map(r => this.translateService.instant('i18n.shared.roles.' + Role[r])).join(', ');
  }

  public override actionComplete(args) {
    super.actionComplete(args);
    if (args.requestType === SfRequestTypes.Save) {
      if (args.action === SfAction.Add) {
        // reset lastAddedUserId since selection of added row was done in super.actionComplete(args)
        this.userManagementService.lastAddedUserId = null;
      }
    }
  }

  public hideClient() {
    return this.authService.getUserInfo().clientId;
  }

  public commandClick(args: CommandClickEventArgs) {
    if (args.commandColumn?.buttonOption?.cssClass?.includes('show-registrations-btn')) {
      this.showRegistrations(args.rowData as UserInfoDTO);
      return;
    }
    if (args.commandColumn?.type === 'None') {
      const user = args.rowData as UserInfoDTO;

      const dialog = this.modalService.showOkCancelDialog('i18n.base.user-management.reset-2fa-dialog.title', 'i18n.base.user-management.reset-2fa-dialog.message');
      dialog.onOkSubject.subscribe(() => {
        this.userManagementService.reset2FARegistration(user.userId).subscribe(() => {
          this.grid.refresh();
        });
      });
    }
  }

  private showRegistrations(user: UserInfoDTO): void {
    const plates = [...new Set(
      (user.controlPlates ?? [])
        .map(p => p.controlPlateNumber?.trim())
        .filter(Boolean)
    )];
    if (!plates.length) { return; }
    this.router.navigate([parkerRegistrationRoute], {
      queryParams: { licensePlate: plates.join(',') }
    });
  }

  protected override getDataToSave(): any {
    const userManagementData = new UserManagementData();
    userManagementData.data = this.mapToUserInfoDTO(this.gridEditForm.getRawValue());
    return userManagementData;
  }

  protected initializeEditForm(data: UserInfoDTO): void {
    this.editDialogTitle = 'i18n.base.user-add-modal.titleEditUser'; // default title
    if (data.roles?.includes(Role.User)) {
      this.editDialogTitle = 'i18n.base.user-add-modal.titleEditUser';
    } else if (data.roles?.includes(Role.ClientUser) || data.roles?.includes(Role.ClientAdmin) || data.roles?.includes(Role.Admin) || data.roles?.includes(Role.CompanyAdmin)) {
      this.editDialogTitle = 'i18n.base.user-add-modal.titleEditWebUser';
    } else if (data.roles?.includes(Role.Proxy)) {
      this.editDialogTitle = 'i18n.base.user-add-modal.titleEditProxy';
    } else if (data.roles?.includes(Role.ParkingRanger)) {
      this.editDialogTitle = 'i18n.base.user-add-modal.titleEditParkingRanger';
    } else if (data.roles?.includes(Role.ThirdPartyParkingControl)) {
      this.editDialogTitle = 'i18n.base.user-add-modal.titleEditThirdPartyUser';
    }

    const clientId = this.authService.getUserInfo().clientId ?? data.clientId;
    // grid always creates dummy object for client
    if (!data.client?.id) {
      data.client = null;
    }

    const client = this.authService.getUserInfo().client ?? data.client ?? this.selectedClient;

    if (!data.userId) {
      // we are in add mode
      data.roles = [this.addUserRole ?? Role.ClientUser];
    }

    if (data.roles.includes(Role.Proxy)) {
      this.gridEditForm = this.fb.group<UserEditFormType>({
        userId: this.fb.control(data.userId),
        firstname: this.fb.control({ value: data.firstname ?? null, disabled: !!data.userId }, [Validators.required]),
        lastname: this.fb.control('Proxy', [Validators.required]),
        password: this.fb.control(null, [CustomValidatorsFactory.passwordValidation()]),
        userName: this.fb.control({ value: data.userName, disabled: true }, [Validators.required]),
        enabled: this.fb.control(data.enabled ?? false),
        validated: this.fb.control(data.validated ?? false),
        client: this.fb.control({ value: client, disabled: !!data.userId }, [Validators.required]),
        companyId: this.fb.control({value: data.companyId, disabled: !!data.userId}),
        role: this.fb.control(!!data.roles?.length ? data.roles[0] : null, [Validators.required]),
      });
      if (!data.userId) {
        // new proxy user -> no roles required
        this.gridEditForm.controls.password.setValidators([Validators.required, CustomValidatorsFactory.passwordValidation()]);
      }
    } else if (data.roles.includes(Role.ThirdPartyParkingControl)) {
      this.gridEditForm = this.fb.group<UserEditFormType>({
        userId: this.fb.control(data.userId),
        firstname: this.fb.control({ value: data.firstname ?? null, disabled: !!data.userId }),
        lastname: this.fb.control('Proxy'),
        password: this.fb.control(null, [CustomValidatorsFactory.passwordValidation()]),
        userName: this.fb.control({ value: data.userName ? data.userName : (client?.clientIdent ? 'TPU_'+ client.clientIdent + '_OMPermissionCheck' : null), disabled: !!data.userId }, [Validators.required]),
        enabled: this.fb.control(data.enabled ?? false),
        validated: this.fb.control(data.validated ?? false),
        client: this.fb.control({ value: client, disabled: !!data.userId }),
        companyId: this.fb.control({value: data.companyId, disabled: !!data.userId}),
        role: this.fb.control(!!data.roles?.length ? data.roles[0] : null),
      });
      if (!data.userId) {
        // new proxy user -> no roles required
        this.gridEditForm.controls.password.setValidators([Validators.required, CustomValidatorsFactory.passwordValidation()]);
      }
    } else {
      const isEditAndIsUser: boolean = (!!data.userId && data.roles[0] === Role.User);
      const isEditAndIsWebUser: boolean = (!!data.userId && (data.roles[0] === Role.Admin || data.roles[0] === Role.ClientAdmin || data.roles[0] === Role.ClientUser));
      const isEditAndIsParkingRanger: boolean = (!!data.userId && data.roles[0] === Role.ParkingRanger);
      this.gridEditForm = this.fb.group<UserEditFormType>({
        userId: this.fb.control(data.userId),
        firstname: this.fb.control({ value: data.firstname ?? null, disabled: isEditAndIsUser }, [Validators.required]),
        lastname: this.fb.control({ value: data.lastname ?? null, disabled: isEditAndIsUser }, [Validators.required]),
        userName: this.fb.control({ value: data.userName ?? null, disabled: true }),
        language: this.fb.control({ value: data.language ?? null, disabled: isEditAndIsUser }, [Validators.required]),
        email: this.fb.control({ value: data.email ?? null, disabled: isEditAndIsUser || isEditAndIsWebUser || isEditAndIsParkingRanger }, [Validators.required, Validators.email]),
        phoneNumber: this.fb.control({ value: data.phoneNumber ?? null, disabled: isEditAndIsUser }, [CustomValidatorsFactory.createPhoneValidator()]),
        fullAddress: this.fb.control({ value: data.fullAddress ?? null, disabled: isEditAndIsUser }),
        addressCompletionId: this.fb.control({ value: null, disabled: isEditAndIsUser }),
        addressAutocompleteSessionToken: this.fb.control({ value: null, disabled: isEditAndIsUser }),
        addressCoordinates: this.fb.control({ value: data.addressCoordinates ?? null, disabled: true }),
        enabled: this.fb.control(data.enabled ?? false),
        validated: this.fb.control(data.validated ?? false),
        client: this.fb.control({ value: client, disabled: !!data.userId }, [Validators.required]),
        companyId: this.fb.control(data.companyId),
        accessCode: this.fb.control(data.accessCode ?? null),
        accessCodeValidUntil: this.fb.control(data.accessCodeValidUntil
          ? data.accessCodeValidUntil.toJSDate()
          : null,
        [CustomValidatorsFactory.accessCodeDateValidator()]),
        accessCodeGeneratedOn: this.fb.control(data.accessCodeGeneratedOn?.toJSDate()),
        accessCodeUsedOn: this.fb.control(data.accessCodeUsedOn?.toJSDate()),
        role: this.fb.control(!!data.roles?.length ? data.roles[0] : null, [Validators.required]),
        controlPlates: this.fb.control(data.controlPlates?.length ? data.controlPlates : []),
        linkedParkers: this.fb.control(data.linkedParkers?.length ? data.linkedParkers : [])
      });

      if (data.roles.includes(Role.ParkingRanger)) {
        this.gridEditForm.controls.phoneNumber.setValidators([Validators.required, CustomValidatorsFactory.createPhoneValidator()]);
        if (!data.userId) {
          // new mobile user -> no roles required
          this.gridEditForm.controls.role.setValidators([]);
        }
      }
    }
  }

  protected getEditDialogTitleParams(rowData): any {
    return {};
  }

  protected override addObjectCompare(addedObject: any, rowObject: any): boolean {
    if (this.userManagementService.lastAddedUserId) {
      return rowObject.userId === this.userManagementService.lastAddedUserId;
    }

    return false;
  }

  private mapToUserInfoDTO(formData: any): UpdateUserInfoDTO | CreateUserDTO | UpdateProxyUserDTO | CreateProxyUserDTO {
    let dto = null;
    if (!formData.userId) {
      if (formData.role === Role.Proxy) {
        dto = {
          firstname : formData.firstname,
          userName : formData.userName,
          clientId : formData.client?.id,
          password : formData.password,
        } as ICreateProxyUserDTO;
        //create workaround for allowing us to pass a field 'roles' with the class without being forced to create a extended class
        //do only use that, when you really know what you are doing.
        dto.roles = [Role.Proxy];
        return dto;
      } else if (formData.role === Role.ThirdPartyParkingControl) {
        dto = {
          firstname : formData.firstname,
          userName : formData.userName,
          clientId : formData.client?.id,
          password : formData.password,
        } as ICreateThirdPartyUserDTO;
        dto.roles = [Role.ThirdPartyParkingControl];
        return dto;
      } else {
        dto = {
        firstname : formData.firstname,
        lastname : formData.lastname,
        userId : formData.userId,
        clientId : formData.client?.id,
        companyId : formData.companyId,
        email : formData.email,
        enabled : formData.enabled,
        language : formData.language,
        phoneNumber : formData.phoneNumber,
        fullAddress : formData.fullAddress,
        addressCompletionId : formData.addressCompletionId,
        addressAutocompleteSessionToken : formData.addressAutocompleteSessionToken,
        roles : [formData.role],
        accessCodeValidUntil : formData.accessCodeValidUntil ? DateTime.fromJSDate(formData.accessCodeValidUntil).endOf('day') : null,
        } as ICreateUserDTO;
        return dto;
      }
    } else {
      if (formData.role === Role.Proxy) {
        dto = {
          userId : formData.userId,
          password : formData.password,
          enabled : formData.enabled,
        } as IUpdateProxyUserDTO;
        //create workaround for allowing us to pass a field 'roles' with the class without being forced to create a extended class
        //do only use that, when you really know what you are doing.
        dto.roles = [Role.Proxy];
        return dto;
      } else if (formData.role === Role.ThirdPartyParkingControl) {
        dto = {
          userId : formData.userId,
          password : formData.password,
          enabled : formData.enabled,
        } as IUpdateThirdPartyUserDTO;
        dto.roles = [Role.ThirdPartyParkingControl];
        return dto;
      } else {
        dto = {
          firstname : formData.firstname,
          lastname : formData.lastname,
          userId : formData.userId,
          clientId : formData.client?.id,
          companyId : formData.companyId,
          email : formData.email,
          enabled : formData.enabled,
          validated : formData.validated,
          language : formData.language,
          phoneNumber : formData.phoneNumber,
          fullAddress : formData.fullAddress,
          addressCompletionId : formData.addressCompletionId,
          addressAutocompleteSessionToken : formData.addressAutocompleteSessionToken,
          roles : [formData.role],
          accessCodeValidUntil : formData.accessCodeValidUntil ? DateTime.fromJSDate(formData.accessCodeValidUntil).endOf('day') : null,
          } as IUpdateUserInfoDTO;
        return dto;
      }
    }
  }
}

